import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  MenuItem,
  Button,
} from '@mui/material';
import { toast } from 'react-toastify';
import { CustomSuccessToast } from '@shared/components/customSuccessToast';
import { CustomErrorToast } from '@shared/components/customErrorToast';
import { showAgentsUsingSettingConflictToast } from '@shared/utils/agentsUsingSettingConflictMessage';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import {
  apiService,
  useVoiceEditControllerEditVoiceMutation,
  useVoiceListControllerVoiceListQuery,
  VoiceDto,
  useVoiceCreateControllerCreateVoiceMutation,
  useVoiceDeleteControllerDeleteVoiceMutation,
  VoiceRequestDto,
} from '@shared/services/apiService/apiService';
import { getApiErrorMessage } from '@shared/utils/apiError.utils';
import { resolveVoiceProviderDisplayName } from '@shared/utils/voiceDisplay';
import type { VoiceFormDraft, VoiceFormField, VoiceFormValues, VoiceSettingChange } from './types';
import { VoiceBasicSettings } from './VoiceBasicSettings';
import { useVoiceSettingsStyles } from './styles';
import { VoiceAdvancedSettings } from './VoiceAdvancedSettings';
import { VoiceConnectionSettings } from './VoiceConnectionSettings';
import { exportVoiceForm, importVoiceForm } from './voiceSettings.transfer';
import { VOICE_PROVIDER_PRESETS } from './voiceSettings.presets';
import {
  type NumericSettingKey,
  JSON_PROMOTED_FIELDS,
  findCredentialKeys,
  formatSettingsJson,
  hydrateVoiceForm,
  isRepresentable,
  parseSettingsJson,
  resolveVoiceFormFields,
  switchProvider,
  writeControlSetting,
} from './voiceSettings.utils';

const EMPTY_FORM: VoiceFormValues = {
  name: '',
  previewUrl: '',
  provider: '',
  providerDisplayName: '',
  voiceId: '',
  voiceSettings: {},
};

/** Typed JSON keeps the user's text until blur; representable top-level copies then move out. */
const promoteJsonFields = (values: VoiceFormValues) => {
  const { fields, settings } = resolveVoiceFormFields(
    {},
    values.voiceSettings,
    JSON_PROMOTED_FIELDS,
  );
  return {
    changed: Object.keys(fields).length > 0,
    form: { ...values, ...fields, voiceSettings: settings },
  };
};

const TOAST_OPTIONS = { closeButton: false, autoClose: 5000, icon: false } as const;

/** Keys under superAdminManageVoice.clipboard used for transfer errors. */
type ClipboardErrorKey = 'error' | 'invalid';

/** A blank credential is an explicit clear; omitted (undefined) keeps the saved value. */
const nullIfBlank = (value: string | null | undefined) =>
  typeof value === 'string' && value.trim() === '' ? null : value;

const useStyles = makeStyles()((theme) => ({
  tableContainer: {
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: theme.shadows[3],
    marginBottom: theme.spacing(4),
    overflowY: 'scroll',
    overflowX: 'hidden',
  },
  tableHeadCell: {
    fontWeight: 'bold',
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
  },
  tableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  tableCell: {
    color: theme.palette.text.secondary,
  },
}));

export const ManageVoiceSettings: React.FC<{}> = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { classes: voiceClasses } = useVoiceSettingsStyles();
  const {
    isLoading: isLoadingVoices,
    refetch: refreshVoices,
    data: voices,
  } = useVoiceListControllerVoiceListQuery();
  const [editVoice] = useVoiceEditControllerEditVoiceMutation();
  const [createVoice] = useVoiceCreateControllerCreateVoiceMutation();
  const [loadPrivateVoice, { reset: resetPrivateVoice }] =
    apiService.endpoints.voiceControllerGetVoice.useLazyQuery();
  const editRequest = useRef(0);
  const savePendingRef = useRef(false);
  const [savePending, setSavePending] = useState(false);
  const [loadingVoiceId, setLoadingVoiceId] = useState<string>();
  const [clipboardBusy, setClipboardBusy] = useState(false);
  const [pasteConfirmOpen, setPasteConfirmOpen] = useState(false);
  const clipboardOperation = useRef(0);
  // Fields edited while JSON cannot be parsed; their edits win over JSON copies once it parses.
  const editedWhileInvalid = useRef(new Set<VoiceFormField>());
  useEffect(
    () => () => {
      ++clipboardOperation.current;
    },
    [],
  );
  const resetClipboardOperation = () => {
    ++clipboardOperation.current;
    setClipboardBusy(false);
    setPasteConfirmOpen(false);
  };
  const [form, setForm] = useState<VoiceFormValues | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [voiceToDelete, setVoiceToDelete] = useState<VoiceDto | null>(null);
  const [deleteConfirmationInput, setDeleteConfirmationInput] = useState('');
  const [selectedVoice, setSelectedVoice] = useState<VoiceDto | null>(null);
  const [voiceSettingsString, setVoiceSettingsString] = useState('{}');
  const [advancedExpanded, setAdvancedExpanded] = useState(false);
  const [numberDrafts, setNumberDrafts] = useState<Partial<Record<NumericSettingKey, string>>>({});
  const [requiredShown, setRequiredShown] = useState(false);
  const voiceProvider = form?.provider ?? '';
  const savedProvider = selectedVoice ? (hydrateVoiceForm(selectedVoice).provider ?? '') : '';
  const providerOptions = Array.from(
    new Set([
      ...VOICE_PROVIDER_PRESETS,
      ...(savedProvider ? [savedProvider] : []),
      ...(voiceProvider ? [voiceProvider] : []),
    ]),
  );
  const jsonResult = parseSettingsJson(voiceSettingsString);
  const jsonInvalid = Boolean(jsonResult.error);
  const credentialKeys = jsonResult.value ? findCredentialKeys(jsonResult.value) : [];
  const jsonError = jsonResult.error
    ? t(`superAdminManageVoice.advanced.${jsonResult.error}`)
    : credentialKeys.length > 0
      ? t('superAdminManageVoice.advanced.credentialsInJson', { keys: credentialKeys.join(', ') })
      : undefined;
  const requiredError = t('superAdminManageVoice.form.required');
  const nameError = requiredShown && !form?.name.trim() ? requiredError : undefined;
  const providerError = requiredShown && !voiceProvider ? requiredError : undefined;
  const hasFormValues = Boolean(
    form &&
    (Object.entries(form).some(
      ([key, value]) =>
        key !== 'voiceSettings' && value !== undefined && value !== null && value !== '',
    ) ||
      Object.keys(form.voiceSettings).length > 0 ||
      Object.keys(numberDrafts).length > 0 ||
      (!jsonResult.value && voiceSettingsString.length > 0)),
  );

  const showError = (message: string) =>
    toast.error((props) => <CustomErrorToast {...props} message={message} />, TOAST_OPTIONS);

  const showApiError = (error: unknown) =>
    showError(
      getApiErrorMessage(
        error,
        error instanceof Error ? error.message : t('superAdminManageVoice.genericError'),
      ),
    );

  const transferError = (key: ClipboardErrorKey = 'error') =>
    showError(t(`superAdminManageVoice.clipboard.${key}`));

  const handleEditVoice = async (listedVoice: VoiceDto) => {
    resetClipboardOperation();
    const request = ++editRequest.current;
    setLoadingVoiceId(listedVoice.id);
    try {
      // The catalog may use a provider alias and deliberately cannot supply saved keys.
      const voice = await loadPrivateVoice({ id: listedVoice.id }, false).unwrap();
      if (request !== editRequest.current) return;
      // JSON copies win as in Worker, so legacy rows keep their effective runtime values.
      setSelectedVoice(voice);
      applyForm(hydrateVoiceForm(voice));
      setAdvancedExpanded(false);
    } catch {
      if (request === editRequest.current) showError(t('superAdminManageVoice.loadError'));
    } finally {
      if (request === editRequest.current) {
        resetPrivateVoice();
        setLoadingVoiceId(undefined);
      }
    }
  };

  const openDeleteDialog = (voice: VoiceDto) => {
    setVoiceToDelete(voice);
    setDeleteConfirmationInput('');
    setDeleteOpen(true);
  };

  const closeDeleteDialog = () => {
    setVoiceToDelete(null);
    setDeleteConfirmationInput('');
    setDeleteOpen(false);
  };

  const [deleteVoice] = useVoiceDeleteControllerDeleteVoiceMutation();

  const handleDeleteConfirmed = async () => {
    if (!voiceToDelete) return;
    const expected = t('superAdminManageVoice.deleteConfirmationPhrase', {
      name: voiceToDelete.name,
    });
    if (deleteConfirmationInput.trim() !== expected) return;

    try {
      await deleteVoice({ id: voiceToDelete.id }).unwrap();
      refreshVoices();
      toast.success(CustomSuccessToast, {
        ...TOAST_OPTIONS,
        data: {
          title: t('superAdminManageVoice.voiceRemovedTitle'),
          description: t('superAdminManageVoice.voiceRemovedDescription', {
            name: voiceToDelete.name,
          }),
        },
      });
      closeDeleteDialog();
    } catch (error: unknown) {
      if (
        showAgentsUsingSettingConflictToast(error, t, 'superAdminManageVoice.voiceUsedByAgents')
      ) {
        return;
      }
      showApiError(error);
      closeDeleteDialog();
    }
  };

  /** Invalidates pending loads, saves and clipboard work; `applyForm` resets the draft itself. */
  const resetDialogState = () => {
    resetClipboardOperation();
    ++editRequest.current;
    savePendingRef.current = false;
    setSavePending(false);
    setSelectedVoice(null);
  };

  const handleCreateVoiceClick = () => {
    resetDialogState();
    setLoadingVoiceId(undefined);
    applyForm(EMPTY_FORM);
    setAdvancedExpanded(false);
  };

  const handleClose = () => {
    resetDialogState();
    setForm(null);
    setVoiceSettingsString('{}');
    resetPrivateVoice();
  };

  const handleSave = async () => {
    if (!form || clipboardBusy || savePendingRef.current) return;
    const settingsInvalid = Boolean(jsonError) || Object.keys(numberDrafts).length > 0;
    const values = settingsInvalid ? form : promoteJsonFields(form).form;
    const missingRequired = !values.name.trim() || !values.provider;
    if (settingsInvalid) setAdvancedExpanded(true);
    if (missingRequired) setRequiredShown(true);
    if (settingsInvalid || missingRequired) return;

    const voiceRequestDto: VoiceRequestDto = {
      ...values,
      name: values.name.trim(),
      providerDisplayName: values.providerDisplayName?.trim() || values.provider,
      apiKey: nullIfBlank(values.apiKey),
      baseUrl: nullIfBlank(values.baseUrl),
    };

    const request = ++editRequest.current;
    savePendingRef.current = true;
    setSavePending(true);
    try {
      if (selectedVoice) {
        await editVoice({ id: selectedVoice.id, voiceRequestDto }).unwrap();
      } else {
        await createVoice({ voiceRequestDto }).unwrap();
      }

      if (request !== editRequest.current) return;
      toast.success(CustomSuccessToast, {
        ...TOAST_OPTIONS,
        data: {
          title: t(
            selectedVoice
              ? 'superAdminManageVoice.voiceEditedTitle'
              : 'superAdminManageVoice.voiceCreatedTitle',
          ),
          description: t(
            selectedVoice
              ? 'superAdminManageVoice.voiceEditedDescription'
              : 'superAdminManageVoice.voiceCreatedDescription',
            { name: voiceRequestDto.name },
          ),
        },
      });
      refreshVoices();
      handleClose();
    } catch (error: unknown) {
      if (request === editRequest.current) showApiError(error);
    } finally {
      if (request === editRequest.current) {
        savePendingRef.current = false;
        setSavePending(false);
      }
    }
  };

  const updateField = <K extends Exclude<keyof VoiceFormValues, 'voiceSettings'>>(
    key: K,
    value: VoiceFormValues[K],
  ) => {
    if (!form) return;
    const settings = { ...form.voiceSettings };
    // An explicit field edit replaces the JSON copy that would otherwise override it.
    const duplicate = isRepresentable(key, settings[key]);
    if (duplicate) delete settings[key];
    if (jsonInvalid) editedWhileInvalid.current.add(key);
    setForm({ ...form, [key]: value, voiceSettings: settings });
    if (duplicate && !jsonInvalid) setVoiceSettingsString(formatSettingsJson(settings));
  };

  const handleJsonChange = (text: string) => {
    setVoiceSettingsString(text);
    const parsed = parseSettingsJson(text);
    if (!form || !parsed.value) return;
    const settings = { ...parsed.value };
    let dropped = false;
    for (const key of editedWhileInvalid.current) {
      if (isRepresentable(key, settings[key])) {
        delete settings[key];
        dropped = true;
      }
    }
    editedWhileInvalid.current.clear();
    setForm({ ...form, voiceSettings: settings });
    // Keep the text in sync only when an edited field's stale copy was removed from it.
    if (dropped) setVoiceSettingsString(formatSettingsJson(settings));
    setNumberDrafts({});
  };

  const handleJsonBlur = () => {
    if (!form || jsonInvalid) return;
    const promoted = promoteJsonFields(form);
    if (!promoted.changed) return;
    setForm(promoted.form);
    setVoiceSettingsString(formatSettingsJson(promoted.form.voiceSettings));
  };

  const handleProviderChange = (provider: string) => {
    if (!form || provider === voiceProvider) return;
    const next = switchProvider(form, provider);
    setForm(next);
    // Keep the user's JSON formatting when the switch left the settings untouched.
    if (next.voiceSettings !== form.voiceSettings)
      setVoiceSettingsString(formatSettingsJson(next.voiceSettings));
    setNumberDrafts({});
  };

  const handleSettingChange: VoiceSettingChange = (key, value) => {
    if (!form || jsonInvalid) return;
    const settings = writeControlSetting(form.voiceSettings, form.provider, key, value);
    setForm({ ...form, voiceSettings: settings });
    setVoiceSettingsString(formatSettingsJson(settings));
    setNumberDrafts((current) => {
      const next = { ...current };
      // Only numeric controls can own a numeric input draft.
      if (Object.hasOwn(next, key)) delete next[key as NumericSettingKey];
      return next;
    });
  };

  const applyForm = (values: VoiceFormDraft) => {
    const next: VoiceFormValues = {
      ...values,
      name: values.name ?? '',
      previewUrl: values.previewUrl ?? '',
      provider: values.provider ?? '',
      providerDisplayName: values.providerDisplayName ?? '',
    };
    setForm(next);
    setVoiceSettingsString(formatSettingsJson(next.voiceSettings));
    setNumberDrafts({});
    setRequiredShown(false);
    editedWhileInvalid.current.clear();
  };

  const handleCopy = async () => {
    if (!form || jsonError || Object.keys(numberDrafts).length > 0) {
      setAdvancedExpanded(true);
      transferError('invalid');
      return;
    }
    const operation = ++clipboardOperation.current;
    setClipboardBusy(true);
    try {
      const exported = promoteJsonFields(form).form;
      await navigator.clipboard.writeText(exportVoiceForm(exported));
      if (operation === clipboardOperation.current)
        toast.success(
          t(
            exported.apiKey?.trim()
              ? 'superAdminManageVoice.clipboard.copiedWithKey'
              : 'superAdminManageVoice.clipboard.copied',
          ),
        );
    } catch {
      if (operation === clipboardOperation.current) transferError();
    } finally {
      if (operation === clipboardOperation.current) setClipboardBusy(false);
    }
  };

  const pasteReplacement = async () => {
    setPasteConfirmOpen(false);
    const operation = ++clipboardOperation.current;
    setClipboardBusy(true);
    try {
      const text = await navigator.clipboard.readText();
      if (operation !== clipboardOperation.current) return;
      const imported = importVoiceForm(text);
      if (!imported.value) {
        transferError();
        return;
      }
      // Commit only after the complete input resolves. No mutation or autosave here.
      applyForm(
        imported.kind === 'settings' && form ? { ...form, ...imported.value } : imported.value,
      );
      toast.success(t('superAdminManageVoice.clipboard.pasted'));
    } catch {
      if (operation === clipboardOperation.current) transferError();
    } finally {
      if (operation === clipboardOperation.current) setClipboardBusy(false);
    }
  };

  const handlePaste = () => {
    if (clipboardBusy || savePendingRef.current) return;
    if (hasFormValues) setPasteConfirmOpen(true);
    else void pasteReplacement();
  };

  if (isLoadingVoices) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (voices) {
    return (
      <>
        <Box
          sx={{
            height: '50px !important',
            display: 'flex',
            justifyContent: 'flex-end',
            my: 2,
            alignItems: 'center',
          }}>
          <Button variant="contained" color="primary" onClick={handleCreateVoiceClick}>
            {t('superAdminManageVoice.createVoice')}
          </Button>
        </Box>
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeadCell}>
                  {t('superAdminManageVoice.table.name')}
                </TableCell>
                <TableCell className={classes.tableHeadCell}>
                  {t('superAdminManageVoice.table.gender')}
                </TableCell>
                <TableCell className={classes.tableHeadCell}>
                  {t('superAdminManageVoice.table.accent')}
                </TableCell>
                <TableCell className={classes.tableHeadCell}>
                  {t('superAdminManageVoice.table.providerDisplay')}
                </TableCell>
                <TableCell className={classes.tableHeadCell}>
                  {t('superAdminManageVoice.table.preview')}
                </TableCell>
                <TableCell className={classes.tableHeadCell}>
                  {t('superAdminManageVoice.table.actions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {voices.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className={classes.tableCell}>
                    <Typography variant="body1">{t('superAdminManageVoice.noData')}</Typography>
                  </TableCell>
                </TableRow>
              )}
              {voices.map((voice) => (
                <TableRow key={voice.id} className={classes.tableRow}>
                  <TableCell className={classes.tableCell}>
                    <Typography variant="body1" color="textPrimary">
                      {voice.name}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Typography variant="body1" color="textPrimary">
                      {voice.gender} {voice.gender === 'male' ? '👨' : '👩'}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Typography variant="body1" color="textPrimary">
                      {voice.accent}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Typography variant="body1" color="textPrimary">
                      {resolveVoiceProviderDisplayName(voice)}
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <audio controls>
                      <source src={voice.previewUrl} type="audio/mpeg" />
                      {t('superAdminManageVoice.audioFallback')}
                    </audio>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Button
                      onClick={() => handleEditVoice(voice)}
                      disabled={Boolean(loadingVoiceId)}
                      sx={{ mr: 1 }}>
                      {loadingVoiceId === voice.id
                        ? t('superAdminManageVoice.loadingVoice')
                        : t('superAdminManageVoice.edit')}
                    </Button>
                    <Button color="error" onClick={() => openDeleteDialog(voice)}>
                      {t('superAdminManageVoice.delete')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {form && (
          <Dialog
            open
            onClose={savePending ? undefined : handleClose}
            fullWidth
            maxWidth="lg"
            aria-labelledby="voice-settings-title">
            <Box className={voiceClasses.dialogHeader}>
              <DialogTitle id="voice-settings-title" className={voiceClasses.dialogTitle}>
                {selectedVoice
                  ? t('superAdminManageVoice.editTitle')
                  : t('superAdminManageVoice.createTitle')}
              </DialogTitle>
            </Box>
            <DialogContent>
              <Box className={voiceClasses.fieldsGrid}>
                <TextField
                  margin="dense"
                  label={t('superAdminManageVoice.form.name')}
                  disabled={clipboardBusy}
                  required
                  error={Boolean(nameError)}
                  helperText={nameError}
                  fullWidth
                  value={form.name}
                  onChange={(e) => updateField('name', e.target.value)}
                />
                <TextField
                  margin="dense"
                  label={t('superAdminManageVoice.form.previewUrl')}
                  disabled={clipboardBusy}
                  fullWidth
                  value={form.previewUrl}
                  onChange={(e) => updateField('previewUrl', e.target.value)}
                />
                <TextField
                  margin="dense"
                  label={t('superAdminManageVoice.form.gender')}
                  disabled={clipboardBusy}
                  fullWidth
                  select
                  value={form.gender ?? ''}
                  onChange={(e) => updateField('gender', e.target.value)}>
                  <MenuItem value="">{t('superAdminManageVoice.form.none')}</MenuItem>
                  <MenuItem value="male">{t('superAdminManageVoice.form.male')}</MenuItem>
                  <MenuItem value="female">{t('superAdminManageVoice.form.female')}</MenuItem>
                </TextField>
                <TextField
                  margin="dense"
                  label={t('superAdminManageVoice.form.accent')}
                  disabled={clipboardBusy}
                  fullWidth
                  value={form.accent ?? ''}
                  onChange={(e) => updateField('accent', e.target.value)}
                />
              </Box>
              <Box className={voiceClasses.providerGroup}>
                <VoiceBasicSettings
                  provider={voiceProvider}
                  providerOptions={providerOptions}
                  voiceId={form.voiceId}
                  model={form.model}
                  language={form.language}
                  disabled={jsonInvalid || clipboardBusy}
                  providerError={providerError}
                  providerDisplayNameField={
                    <TextField
                      margin="dense"
                      label={t('superAdminManageVoice.form.providerDisplayName')}
                      disabled={clipboardBusy}
                      fullWidth
                      value={form.providerDisplayName}
                      onChange={(e) => updateField('providerDisplayName', e.target.value)}
                      helperText={t('superAdminManageVoice.form.providerDisplayNameHelper', {
                        provider:
                          voiceProvider || (t('superAdminManageVoice.form.provider') as string),
                      })}
                    />
                  }
                  onProviderChange={handleProviderChange}
                  onVoiceIdChange={(value) => updateField('voiceId', value)}
                  onSettingChange={(key, value) => updateField(key, value ?? null)}
                />
              </Box>
              <VoiceConnectionSettings
                apiKey={form.apiKey ?? ''}
                baseUrl={form.baseUrl ?? ''}
                disabled={clipboardBusy}
                onChange={updateField}
              />
              <VoiceAdvancedSettings
                disabled={clipboardBusy}
                provider={voiceProvider}
                settings={form.voiceSettings}
                rawJson={voiceSettingsString}
                jsonError={jsonError}
                jsonInvalid={jsonInvalid}
                drafts={numberDrafts}
                expanded={advancedExpanded}
                onExpandedChange={setAdvancedExpanded}
                onJsonChange={handleJsonChange}
                onJsonBlur={handleJsonBlur}
                onSettingChange={handleSettingChange}
                onInvalidNumber={(key, text) =>
                  setNumberDrafts((current) => ({
                    ...current,
                    [key]: text,
                  }))
                }
              />
            </DialogContent>
            <DialogActions className={voiceClasses.dialogActions}>
              <Box
                className={voiceClasses.summaryRow}
                sx={{ mr: 'auto' }}
                aria-busy={clipboardBusy}>
                <Button onClick={handleCopy} disabled={clipboardBusy || savePending}>
                  {t('superAdminManageVoice.clipboard.copy')}
                </Button>
                <Button onClick={handlePaste} disabled={clipboardBusy || savePending}>
                  {t('superAdminManageVoice.clipboard.paste')}
                </Button>
                {clipboardBusy && (
                  <Typography role="status">
                    {t('superAdminManageVoice.clipboard.loading')}
                  </Typography>
                )}
              </Box>
              <Button onClick={handleClose} color="primary" disabled={savePending}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleSave} color="primary" disabled={savePending || clipboardBusy}>
                {savePending ? t('superAdminManageVoice.savePending') : t('common.save')}
              </Button>
            </DialogActions>
          </Dialog>
        )}

        <Dialog
          open={pasteConfirmOpen}
          onClose={() => setPasteConfirmOpen(false)}
          fullWidth
          maxWidth="sm"
          aria-labelledby="voice-paste-confirm-title"
          aria-describedby="voice-paste-confirm-description">
          <DialogTitle id="voice-paste-confirm-title">
            {t('superAdminManageVoice.clipboard.replaceTitle')}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="voice-paste-confirm-description">
              {t('superAdminManageVoice.clipboard.replaceDescription')}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPasteConfirmOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={pasteReplacement} color="primary" variant="contained">
              {t('superAdminManageVoice.clipboard.replaceConfirm')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete confirmation dialog */}
        <Dialog open={deleteOpen} onClose={closeDeleteDialog} fullWidth maxWidth="sm">
          <DialogTitle>{t('superAdminManageVoice.confirmDeleteTitle')}</DialogTitle>
          <DialogContent>
            <Typography>{t('superAdminManageVoice.deleteInstruction')}</Typography>
            <Box sx={{ mt: 1, mb: 1 }}>
              <Typography variant="body2" sx={{ fontFamily: 'Monaco, Menlo, monospace' }}>
                {voiceToDelete
                  ? t('superAdminManageVoice.deleteConfirmationPhrase', {
                      name: voiceToDelete.name,
                    })
                  : ''}
              </Typography>
            </Box>
            <TextField
              margin="dense"
              label={t('superAdminManageVoice.typeConfirmationLabel')}
              fullWidth
              value={deleteConfirmationInput}
              onChange={(e) => setDeleteConfirmationInput(e.target.value)}
              onPaste={(e) => {
                // Disable pasting into the confirmation field to force manual typing
                e.preventDefault();
              }}
              onDrop={(e) => {
                // Disable dropping text into the field
                e.preventDefault();
              }}
              onKeyDown={(e) => {
                // Prevent Cmd/Ctrl+V paste via keyboard shortcuts
                if ((e.ctrlKey || e.metaKey) && (e.key === 'v' || e.key === 'V')) {
                  e.preventDefault();
                }
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDeleteDialog}>{t('common.cancel')}</Button>
            <Button
              color="error"
              onClick={handleDeleteConfirmed}
              disabled={
                !(
                  voiceToDelete &&
                  deleteConfirmationInput.trim() ===
                    t('superAdminManageVoice.deleteConfirmationPhrase', {
                      name: voiceToDelete.name,
                    })
                )
              }>
              {t('superAdminManageVoice.delete')}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return null;
};

export default ManageVoiceSettings;
